// 그림 요청문 짜기. 산문 지침이 지키지 못한 세 가지를 여기서 코드로 지킨다.
// 1) 고정 조건은 살아 있는 것 전부를 글자 그대로 싣는다 (다시 쓰지 않는다)
// 2) 기준 그림이 실제로 붙을 때만 "유지" 문장을 쓴다
// 3) 실린 조건 수를 세어 돌려준다. 하나라도 빠지면 만들지 않는다
import { ANGLE_VIEWS, byId, CAMERAS, EFFECTS, EVENT_KINDS, PEOPLE, PURPOSES, qualityEn, SCENES, STAGE_DESIGN_EN, STAGE_PRESETS, TIMES, VENUE_TYPES } from "./knowledge";
import type { Asset, Constraint, CutRef, ProjectData, SpaceImage, StageDirection } from "./types";

export type Part = { text: string } | { ref: CutRef };
export type Assembled = { parts: Part[]; prompt: string; refs: CutRef[]; constraintCount: number; constraintTotal: number };

export const MAX_ANGLES = 3; // 기준 그림 말고 더 붙이는 다른 방향 그림 수

export const activeConstraints = (d: ProjectData) => d.constraints.filter((c) => c.active);
const isStageImg = (s: SpaceImage) => s.source === "stage" || s.source === "stage-angle";
/** 장소 그림(무대 확정 전의 공간): 올린 그림, 3D 시점, 컷에서 올린 그림 */
export const venueImages = (d: ProjectData) => d.space.filter((s) => !isStageImg(s));
/** 확정한 무대 그림과 그 다른 방향 그림 */
export const stageImages = (d: ProjectData) => d.space.filter(isStageImg);
/** 2차 연출컷에 쓰는 공간 그림: 확정한 무대가 있으면 그 무대 그림들, 없으면 장소 그림들 */
export const sceneSpace = (d: ProjectData) => (stageImages(d).length ? stageImages(d) : venueImages(d));
const baseOf = (list: SpaceImage[]) => list.find((s) => s.base) ?? list[0];
export const baseImage = (d: ProjectData): SpaceImage | undefined => baseOf(sceneSpace(d));
export const venueBase = (d: ProjectData): SpaceImage | undefined => baseOf(venueImages(d));

/** 기준 그림 설명. 3D 모형이면 덩어리 모형이라고, 확정 무대면 승인된 무대라고 알려 준다 */
function baseText(n: number, b: SpaceImage): string {
  if (b.source === "model3d")
    return `Image ${n} — a simple 3D massing model of the venue (${b.label}): white and gray blocks, dark navy panels are LED screens, a flat outlined area marks where the stage stands, small orange figures are people for scale (draw them as real people in varied clothing, not orange). It fixes the camera, the layout, the shapes and the sizes. Turn the blocks into real materials, real lighting and real people at the same scale.`;
  if (b.source === "stage") return `Image ${n} — the approved stage design in its venue (${b.label}). It fixes the stage design, the camera, the positions and the sizes.`;
  return `Image ${n} — venue reference (${b.label}). It fixes the camera, the positions and the sizes of everything in the venue.`;
}

export const MAX_ASSETS = 4;

/** 이 단계에 붙일 소스·에셋 (로고, 키비주얼, LED 화면 그림, 참고 그림) */
export const usedAssets = (d: ProjectData, use: "stage" | "scene") => (d.assets || []).filter((a) => (use === "stage" ? a.useStage : a.useScene)).slice(0, MAX_ASSETS);

function assetText(n: number, a: Asset, d: ProjectData): string {
  const el = a.target ? d.elements.find((e) => e.id === a.target) : undefined;
  switch (a.kind) {
    case "logo":
      return `Image ${n} — the event logo "${a.name}". Show it exactly as given (shape, colors, letters)${a.note ? `, here: ${a.note}` : ", where a logo naturally belongs on the stage or the screens"}. Never redraw, distort or invent letters.`;
    case "kv":
      return `Image ${n} — the event key visual "${a.name}". Use its colors and graphic motifs for the screens and the stage look${a.note ? ` (${a.note})` : ""}. Do not paste it as a poster.`;
    case "led":
      return `Image ${n} — the exact video frame shown on ${el ? `the screen "${el.name}" (${el.en.replace(/\.$/, "")})` : a.note ? a.note : "the main LED screen"}. Display this image on that screen as its content, following the screen's shape and curvature, bright and clearly visible.`;
    default:
      return `Image ${n} — a reference for: ${a.note || a.name}. Use it only for that; do not copy anything else from it.`;
  }
}

function pushAssets(parts: Part[], n: number, d: ProjectData, use: "stage" | "scene"): number {
  for (const a of usedAssets(d, use)) {
    n++;
    parts.push({ text: assetText(n, a, d) });
    parts.push({ ref: { role: "asset", key: a.key, label: a.name } });
  }
  return n;
}

/** LED 에셋 한 장: 무대 사진이 아니라 화면에 띄울 평면 그래픽 그 자체 */
export function assembleAsset(d: ProjectData, o: { desc: string; aspect: string; screen?: string; logoKey?: string }): Assembled {
  const parts: Part[] = [];
  if (o.logoKey) {
    parts.push({ text: "Image 1 — the event logo. Include it exactly as given (shape, colors, letters) only if the content calls for a logo. Never redraw or invent letters." });
    parts.push({ ref: { role: "asset", key: o.logoKey, label: "로고" } });
  }
  parts.push({
    text: [
      `Create one flat 2D motion-graphic frame that will be shown on ${o.screen ? `the ${o.screen}` : "a large LED screen"} at a live event.`,
      "It is the screen content itself, NOT a photo of a screen, a stage or a venue: full-bleed edge to edge, no frame, no device mockup, no audience.",
      `Content: ${o.desc}`,
      `Aspect ratio ${o.aspect}. Bold shapes, rich color and high contrast that read well on a huge LED at night. No made-up words or letters.`,
    ].join("\n"),
  });
  const prompt = parts.map((p) => ("text" in p ? p.text : `[그림: ${p.ref.label}]`)).join("\n\n");
  return { parts, prompt, refs: parts.flatMap((p) => ("ref" in p ? [p.ref] : [])), constraintCount: 0, constraintTotal: 0 };
}

export function constraintBlock(cs: Constraint[]): string {
  if (!cs.length) return "";
  return ["Fixed conditions (every line applies):", ...cs.map((c) => `${c.n}. ${c.en}`)].join("\n");
}

/** 장면 설정을 영어 한 단락으로. AI가 다듬은 글(composed)이 있으면 그것을 쓴다 */
export function sceneText(d: ProjectData): string {
  const s = d.scene;
  if (s.composed?.en) return s.composed.en;
  const bits: string[] = [];
  const scene = byId(SCENES, s.scene);
  if (scene) bits.push(`The scene is ${scene.en}.`);
  const people = s.people.map((id) => byId(PEOPLE, id)?.en).filter(Boolean);
  if (people.length) bits.push(`${people.join("; ")}.`);
  const effects = s.effects.map((id) => byId(EFFECTS, id)?.en).filter(Boolean);
  if (effects.length) bits.push(`Effects: ${effects.join(", ")}.`);
  if (s.screens.trim()) bits.push(`The screens show: ${s.screens.trim()}.`);
  if (s.extra.trim()) bits.push(s.extra.trim());
  return bits.join(" ");
}

function cameraLine(d: ProjectData, hasBase: boolean): string {
  const cam = byId(CAMERAS, d.scene.camera);
  if (hasBase && (!cam || cam.id === "same")) {
    const how = d.analysis?.cameraEn ? ` — ${d.analysis.cameraEn}` : "";
    return `Camera: exactly the camera of Image 1${how}. Horizon level, no tilt, no warped perspective.`;
  }
  if (cam?.en) return `Camera: ${cam.en}.${hasBase ? " Keep the venue layout of Image 1; move the camera, do not rotate or rearrange the venue." : ""}`;
  return "";
}

function finish(parts: Part[], d: ProjectData): Assembled {
  const prompt = parts.map((p) => ("text" in p ? p.text : `[그림: ${p.ref.label}]`)).join("\n\n");
  const cs = activeConstraints(d);
  const missing = cs.filter((c) => !prompt.includes(`${c.n}. ${c.en}`));
  if (missing.length) throw new Error(`고정 조건 ${missing.map((c) => c.n).join(", ")}번이 요청문에 실리지 않았습니다.`);
  const refs = parts.flatMap((p) => ("ref" in p ? [p.ref] : []));
  return { parts, prompt, refs, constraintCount: cs.length - missing.length, constraintTotal: cs.length };
}

/** 새로 뽑기 */
export function assembleNew(d: ProjectData, o: { canonKey?: string } = {}): Assembled {
  const parts: Part[] = [];
  const base = baseImage(d);
  let n = 0;
  if (base) {
    n++;
    parts.push({ text: baseText(n, base) });
    parts.push({ ref: { role: "base", key: base.key, label: base.label } });
    // 소스·에셋을 붙이면 다른 방향 그림은 2장까지만 (붙이는 그림이 너무 많아지지 않게)
    const maxAngles = usedAssets(d, "scene").length ? 2 : MAX_ANGLES;
    for (const a of sceneSpace(d).filter((s) => s.id !== base.id).slice(0, maxAngles)) {
      n++;
      parts.push({ text: `Image ${n} — the same ${base.source === "stage" ? "stage and venue" : "venue"} from another angle (${a.label}). Use it only to understand the 3D space. Do not copy its camera.` });
      parts.push({ ref: { role: "angle", key: a.key, label: a.label } });
    }
  }
  n = pushAssets(parts, n, d, "scene");
  if (o.canonKey) {
    n++;
    parts.push({ text: `Image ${n} — the approved cut of this project. Match only its look: lighting, color grading and crowd density. Do not copy its camera or content.` });
    parts.push({ ref: { role: "canon", key: o.canonKey, label: "승인본" } });
  }
  const purpose = byId(PURPOSES, d.scene.purpose);
  const time = byId(TIMES, d.scene.time);
  const lines: string[] = [];
  lines.push(`Create one image for an event proposal${purpose ? `: ${purpose.en}` : ""}${time ? `, ${time.en}` : ""}.`);
  if (base) lines.push(`Keep the ${base.source === "stage" ? "stage design and the venue" : "venue"} exactly as in Image 1: every structure, its shape, position and size. Do not add structures that are not in Image 1.`);
  const cam = cameraLine(d, Boolean(base));
  if (cam) lines.push(cam);
  const scene = sceneText(d);
  if (scene) lines.push(`Scene: ${scene}`);
  if (d.analysis?.scaleEn) lines.push(`Scale: ${d.analysis.scaleEn}`);
  parts.push({ text: lines.join("\n") });
  const block = constraintBlock(activeConstraints(d));
  if (block) parts.push({ text: block });
  parts.push({ text: `Quality: ${qualityEn(d.scene.tone).join(" ")}` });
  return finish(parts, d);
}

export type FixLine = { letter: string; en: string; label: string; refKey?: string };

/** 고치기: 기준 그림(위치·크기) + 모습 참고 그림(있으면) + 고칠 컷. U대회 보정 1번에서 통한 순서 그대로 */
export function assembleFix(d: ProjectData, target: { key: string }, fixes: FixLine[], o: { phase?: string } = {}): Assembled {
  if (!fixes.length) throw new Error("고칠 곳이 없습니다.");
  const parts: Part[] = [];
  // 무대 시안을 고칠 때는 장소 그림을, 연출컷을 고칠 때는 확정 무대 그림을 기준으로 붙인다
  const base = o.phase === "stage" ? venueBase(d) : baseImage(d);
  let n = 0;
  if (base) {
    n++;
    parts.push({ text: `Image ${n} — venue reference (${base.label}): the true geometry, positions and sizes.` });
    parts.push({ ref: { role: "base", key: base.key, label: base.label } });
  }
  for (const f of fixes.filter((x) => x.refKey)) {
    n++;
    parts.push({ text: `Image ${n} — appearance reference for "${f.label}". Use it ONLY to see what that looks like. Do not copy anything else from it: not its stage, people, colors or camera.` });
    parts.push({ ref: { role: "appearance", key: f.refKey!, label: `${f.label} 참고` } });
  }
  n++;
  const t = n;
  parts.push({ text: `Image ${t} — the cut to edit.` });
  parts.push({ ref: { role: "target", key: target.key, label: "고칠 컷" } });
  const lines = [
    `Edit Image ${t}. Change only the ${fixes.length === 1 ? "point" : `${fixes.length} points`} below. Keep everything else in Image ${t} exactly as it is: the camera, the structures, the crowd, the lighting and the color.`,
    ...fixes.map((f) => `${f.letter}. ${f.en}`),
  ];
  parts.push({ text: lines.join("\n") });
  const block = constraintBlock(activeConstraints(d));
  if (block) parts.push({ text: block });
  parts.push({ text: `Quality: ${qualityEn(d.scene.tone).join(" ")}` });
  return finish(parts, d);
}

// ───────── 1차: 무대 디자인 ─────────

/** 무대 시안 한 장: 장소 그림(있으면) 위에 디자인 방향 하나로 무대를 디자인한다 */
export function assembleStage(d: ProjectData, dir: StageDirection): Assembled {
  const parts: Part[] = [];
  const base = venueBase(d);
  let n = 0;
  if (base) {
    n++;
    parts.push({ text: `${baseText(n, base)} Keep this venue, its camera and its scale. The stage itself is NOT designed yet: design a new stage and place it in the stage area.` });
    parts.push({ ref: { role: "base", key: base.key, label: base.label } });
    for (const a of venueImages(d).filter((s) => s.id !== base.id).slice(0, 2)) {
      n++;
      parts.push({ text: `Image ${n} — the same venue from another angle (${a.label}). Use it only to understand the space. Do not copy its camera.` });
      parts.push({ ref: { role: "angle", key: a.key, label: a.label } });
    }
  }
  n = pushAssets(parts, n, d, "stage");
  const b = d.brief;
  const venue = byId(VENUE_TYPES, b.venueType);
  const kind = byId(EVENT_KINDS, b.eventKind);
  const form = d.stage?.form ? STAGE_PRESETS.find((s) => s.id === d.stage!.form) : undefined;
  const lines = [
    `Create one stage design proposal image for ${kind?.en || "an event"}${b.eventType ? ` (${b.eventType})` : ""}${venue?.en ? ` in a ${venue.en}` : ""}${b.capacity ? ` for about ${b.capacity.toLocaleString("en-US")} people` : ""}.`,
    `Stage design direction: ${dir.en}`,
    form && !form.spot ? `Overall layout to start from: ${form.en}.` : "",
    base ? "" : "Choose a clear three-quarter view from the audience side that shows the whole stage.",
    STAGE_DESIGN_EN,
    "The stage must be buildable: realistic structure, truss, LED panels and materials at a believable scale.",
  ].filter(Boolean);
  parts.push({ text: lines.join("\n") });
  const block = constraintBlock(activeConstraints(d));
  if (block) parts.push({ text: block });
  parts.push({ text: `Quality: ${qualityEn(d.scene.tone).join(" ")}` });
  return finish(parts, d);
}

/** 확정한 무대를 다른 방향에서: 디자인은 그대로, 카메라만 바꾼다 */
export function assembleStageAngle(d: ProjectData, approvedKey: string, viewId: string): Assembled {
  const view = byId(ANGLE_VIEWS, viewId);
  if (!view) throw new Error("시점을 찾지 못했습니다.");
  const parts: Part[] = [
    { text: "Image 1 — the approved stage design. Keep this exact stage: the same structure, shape, proportions, screens, materials, colors and lighting design." },
    { ref: { role: "base", key: approvedKey, label: "확정 무대" } },
  ];
  const venue = venueBase(d);
  if (venue) {
    parts.push({ text: `Image 2 — the venue (${venue.label}), for the surroundings and the scale only. Do not copy its camera.` });
    parts.push({ ref: { role: "angle", key: venue.key, label: venue.label } });
  }
  parts.push({ text: `Show the same stage in the same venue from ${view.en}. Do not redesign anything; only the camera changes. Same time of day and the same lighting.` });
  const block = constraintBlock(activeConstraints(d));
  if (block) parts.push({ text: block });
  parts.push({ text: `Quality: ${qualityEn(d.scene.tone).join(" ")}` });
  return finish(parts, d);
}

/** 장면 설정이 바뀌었는지 보는 지문. 바뀌면 AI가 장면 글을 다시 쓴다 */
export function sceneHash(d: ProjectData): string {
  const s = d.scene;
  const els = d.elements.filter((e) => e.status !== "proposed").map((e) => `${e.id}:${e.status}:${e.en}`);
  const assets = usedAssets(d, "scene").map((a) => `${a.id}:${a.kind}:${a.target || ""}:${a.note}`);
  const raw = JSON.stringify([s.scene, s.purpose, s.time, s.camera, s.people, s.effects, s.screens, s.extra, s.tone, d.brief, els, assets]);
  let h = 0;
  for (let i = 0; i < raw.length; i++) h = (Math.imul(31, h) + raw.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
